import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { usePageMeta } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { NICHES, PLATFORMS, inputCls, btnGold } from "@/lib/creators";

const EMPTY = { displayName: "", handle: "", email: "", niche: "", platform: "", audienceSize: "", socialHandles: "", contentLink: "", whyAstra: "", disclosure: false, website: "" };

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-sm text-[#D6D0C5] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[#A7B0C0] mt-1">{hint}</span>}
    </label>
  );
}

function HandleStatus({ handle }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!handle) return setState(null);
    if (!/^[a-z0-9]{3,20}$/.test(handle)) return setState("invalid");
    setState("checking");
    const t = setTimeout(() => api.get("/creators/handle-available", { params: { handle } })
      .then(({ data }) => setState(data.available ? "ok" : "taken")).catch(() => setState(null)), 350);
    return () => clearTimeout(t);
  }, [handle]);
  const msg = { invalid: [X, "3–20 lowercase letters or numbers", "text-red-300"], taken: [X, "That handle is taken", "text-red-300"],
    ok: [Check, `astralogico.com/c/${handle} is available`, "text-emerald-300"], checking: [Loader2, "Checking…", "text-[#A7B0C0]"] }[state];
  if (!msg) return null;
  const [Icon, text, cls] = msg;
  return <span data-testid="apply-handle-status" className={`flex items-center gap-1 text-xs mt-1 ${cls}`}><Icon size={12} className={state === "checking" ? "animate-spin" : ""} /> {text}</span>;
}

export default function CreatorApply() {
  usePageMeta("Apply · Astra Creator Program", "/creators/apply", "Apply to become a founding Astra creator and earn 40% commission sharing your daily horoscope.");
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => { if (k === "disclosure") setErr(""); setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : k === "handle" ? e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") : e.target.value })); };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.disclosure) return setErr("Please agree to disclose #ad on sponsored posts.");
    setBusy(true);
    try {
      await api.post("/creators/apply", { ...f, audienceSize: Number(f.audienceSize) || 0 });
      navigate("/creators/thank-you", { state: { handle: f.handle } });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <PublicShell testId="creator-apply-page">
      <form onSubmit={submit} className="px-6 pt-6 max-w-xl lg:mx-auto space-y-4 fade-up" data-testid="creator-apply-form">
        <p className="text-[11px] uppercase tracking-[0.35em] gold-text">Creator Program</p>
        <h1 className="text-4xl font-light">Apply for a founding spot</h1>
        <p className="text-sm text-[#A7B0C0]">We review every application by hand. <Link to="/creators" className="gold-text underline underline-offset-4">How the program works</Link></p>
        <Field label="Your name or creator name"><input data-testid="apply-display-name" required minLength={2} maxLength={50} value={f.displayName} onChange={set("displayName")} className={inputCls} /></Field>
        <Field label="Desired handle" hint="Your page will be astralogico.com/c/yourhandle">
          <input data-testid="apply-handle" required value={f.handle} onChange={set("handle")} maxLength={20} placeholder="mysticmaya" className={inputCls} />
          <HandleStatus handle={f.handle} />
        </Field>
        <Field label="Email"><input data-testid="apply-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Niche">
            <select data-testid="apply-niche" required value={f.niche} onChange={set("niche")} className={inputCls}>
              <option value="">Choose…</option>{NICHES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Primary platform">
            <select data-testid="apply-platform" required value={f.platform} onChange={set("platform")} className={inputCls}>
              <option value="">Choose…</option>{PLATFORMS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Follower count (primary platform)"><input data-testid="apply-audience" type="number" min={0} required value={f.audienceSize} onChange={set("audienceSize")} className={inputCls} /></Field>
        <Field label="Social handles" hint="e.g. @maya on Instagram, @maya.stars on TikTok"><input data-testid="apply-socials" maxLength={300} value={f.socialHandles} onChange={set("socialHandles")} className={inputCls} /></Field>
        <Field label="Link to a post you're proud of"><input data-testid="apply-content-link" type="url" maxLength={300} value={f.contentLink} onChange={set("contentLink")} placeholder="https://" className={inputCls} /></Field>
        <Field label="Why do you want to join Astra?"><textarea data-testid="apply-why" required minLength={10} maxLength={2000} rows={4} value={f.whyAstra} onChange={set("whyAstra")} className={inputCls} /></Field>
        <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
        <label className="flex items-start gap-3 text-sm text-[#D6D0C5]">
          <input data-testid="apply-disclosure" type="checkbox" checked={f.disclosure} onChange={set("disclosure")} className="mt-1 accent-[#D4AF37] w-4 h-4" />
          I will disclose #ad on every sponsored post, as required by the ASA (UK) and FTC (US).
        </label>
        {err && <p data-testid="apply-error" className="text-sm text-red-300">{err}</p>}
        <button data-testid="apply-submit" disabled={busy} className={`${btnGold} w-full text-lg py-3.5`}>
          {busy ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Submit application"}
        </button>
      </form>
    </PublicShell>
  );
}
